
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUserPlus, FaEdit, FaTrash, FaEye, FaUser, FaFileImport } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';
import '../styles/LabAdminDashboard.css';

const UserListings = ({ 
  approvedUsers = [], 
  assetUsers = [], 
  onAddTechnician, 
  onAddAssistant, 
  onAddAssetUser,
  onImportUsers,
  onRefresh 
}) => {
  const [usersView, setUsersView] = useState('technicians');
  const [usersPage, setUsersPage] = useState(1);
  const usersPerPage = 6;
  const [viewUser, setViewUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ firstName: '', lastName: '', email: '' });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Helper function to display role name
  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'asset_technician':
        return 'Technician';
      case 'asset_incharge':
        return 'Asset Incharge';
      case 'asset_user':
        return 'Asset User';
      default:
        return role;
    }
  };

  const filteredUsers = (() => {
    if (usersView === 'all') {
      return [...approvedUsers, ...assetUsers.filter(u => u.role === 'asset_user')];
    }
    if (usersView === 'technicians') {
      return approvedUsers.filter(u => u.role === 'asset_technician');
    }
    if (usersView === 'asset_incharge') {
      return approvedUsers.filter(u => u.role === 'asset_incharge');
    }
    if (usersView === 'asset_user') {
      return assetUsers.filter(u => u.role === 'asset_user');
    }
    return [];
  })();

  const totalUserPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));

  useEffect(() => {
    if (usersPage > totalUserPages) setUsersPage(1);
  }, [filteredUsers.length, totalUserPages, usersPage]);

  const usersToShow = filteredUsers.slice((usersPage - 1) * usersPerPage, usersPage * usersPerPage);

  const startEditUser = (u) => {
    setEditingUser(u._id);
    setEditUserForm({ firstName: u.firstName || '', lastName: u.lastName || '', email: u.email || '' });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(`${API_ENDPOINTS.LAB_ADMIN_USERS}/${editingUser}`, editUserForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('User updated successfully');
      setEditingUser(null);
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating user:', err.response || err.message);
      setError(err.response?.data?.message || 'Error updating user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_ENDPOINTS.LAB_ADMIN_USERS}/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('User deleted successfully');
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err.response || err.message);
      setError(err.response?.data?.message || 'Error deleting user');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <div className="section-content">
      <div className="section-header-admin">
        <div>
          <h1>Manage Users</h1>
          <p>View, edit or remove technicians and assistants</p>
        </div>
        <div className="header-button-group">
          <button className="btn-add-technician" onClick={onAddTechnician}>
            <FaUserPlus /> Add Asset Technician
          </button>
          <button className="btn-add-incharge" onClick={onAddAssistant}>
            <FaUserPlus /> Add Asset Incharge
          </button>
          <button className="btn-add-assetuser" onClick={onAddAssetUser}>
            <FaUserPlus /> Add Asset User
          </button>
          <button className="btn-import-users" onClick={onImportUsers} title="Import users from CSV/Excel file">
            <FaFileImport /> Import Users
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <div className="manage-users-tabs">
        <div className="tabs-controls">
          <button 
            className={`tab ${usersView === 'technicians' ? 'active' : ''}`} 
            onClick={() => { setUsersView('technicians'); setUsersPage(1); }}
          >
            Asset Technicians ({approvedUsers.filter(u => u.role === 'asset_technician').length})
          </button>
          <button 
            className={`tab ${usersView === 'asset_incharge' ? 'active' : ''}`} 
            onClick={() => { setUsersView('asset_incharge'); setUsersPage(1); }}
          >
            Asset Incharge ({approvedUsers.filter(u => u.role === 'asset_incharge').length})
          </button>
          <button 
            className={`tab ${usersView === 'all' ? 'active' : ''}`} 
            onClick={() => { setUsersView('all'); setUsersPage(1); }}
          >
            All ({approvedUsers.length + assetUsers.filter(u => u.role === 'asset_user').length})
          </button>
          <button 
            className={`tab ${usersView === 'asset_user' ? 'active' : ''}`} 
            onClick={() => { setUsersView('asset_user'); setUsersPage(1); }}
          >
            Asset Users ({assetUsers.filter(u => u.role === 'asset_user').length})
          </button>
        </div>

        {/* Edit User Modal/Form */}
        {editingUser && (
          <div className="modal-overlay" style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              minWidth: '400px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
            }}>
              <h2>Edit User</h2>
              <form onSubmit={handleUpdateUser}>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>First Name</label>
                  <input
                    type="text"
                    value={editUserForm.firstName}
                    onChange={(e) => setEditUserForm({...editUserForm, firstName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={editUserForm.lastName}
                    onChange={(e) => setEditUserForm({...editUserForm, lastName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '24px' }}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={editUserForm.email}
                    onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="submit" className="submit-btn" style={{ flex: 1 }}>
                    Update User
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setEditingUser(null)}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      backgroundColor: '#e0e6ed',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View User Modal */}
        {viewUser && (
          <div className="modal-overlay" style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              minWidth: '400px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                {viewUser.profilePic ? (
                  <img 
                    src={viewUser.profilePic} 
                    alt={viewUser.firstName}
                    style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#e0f2ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0052cc',
                    fontSize: '24px'
                  }}>
                    <FaUser />
                  </div>
                )}
                <div>
                  <h2 style={{ margin: '0 0 5px 0' }}>{viewUser.firstName} {viewUser.lastName}</h2>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{getRoleDisplayName(viewUser.role)}</p>
                </div>
              </div>
              <p><strong>Email:</strong> {viewUser.email}</p>
              <button 
                onClick={() => setViewUser(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e0e6ed',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginTop: '16px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Users Table */}
        {usersView !== 'asset_user' && (
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Name</th>
                  <th style={{ width: '30%' }}>Email</th>
                  <th style={{ width: '20%' }}>Role</th>
                  <th style={{ width: '25%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersToShow.length > 0 ? (
                  usersToShow.map((u) => (
                    <tr key={u._id}>
                      <td style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {u.profilePic ? (
                          <img 
                            src={u.profilePic} 
                            alt={u.firstName}
                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#e0f2ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#0052cc'
                          }}>
                            <FaUser size={14} />
                          </div>
                        )}
                        <span>{u.firstName} {u.lastName}</span>
                      </td>
                      <td>{u.email}</td>
                      <td>{getRoleDisplayName(u.role)}</td>
                      <td>
                        <button className="btn-sm" onClick={() => setViewUser(u)} title="View"><FaEye /></button>
                        <button className="btn-sm" onClick={() => startEditUser(u)} title="Edit"><FaEdit /></button>
                        <button className="btn-sm btn-danger" onClick={() => handleDeleteUser(u._id)} title="Delete"><FaTrash /></button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                      No users found in this category
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {totalUserPages > 1 && (
              <div className="pagination">
                <button disabled={usersPage <= 1} onClick={() => setUsersPage(p => Math.max(1, p - 1))}>Prev</button>
                <span>Page {usersPage} / {totalUserPages}</span>
                <button disabled={usersPage >= totalUserPages} onClick={() => setUsersPage(p => Math.min(totalUserPages, p + 1))}>Next</button>
              </div>
            )}
          </div>
        )}

        {/* Asset Users Table */}
        {usersView === 'asset_user' && (
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Name</th>
                  <th style={{ width: '30%' }}>Email</th>
                  <th style={{ width: '20%' }}>Role</th>
                  <th style={{ width: '25%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersToShow && usersToShow.length > 0 ? (
                  usersToShow.map((u) => (
                    <tr key={u._id}>
                      <td style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {u.profilePic ? (
                          <img 
                            src={u.profilePic} 
                            alt={u.firstName}
                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#e0f2ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#0052cc'
                          }}>
                            <FaUser size={14} />
                          </div>
                        )}
                        <span>{u.firstName} {u.lastName}</span>
                      </td>
                      <td>{u.email}</td>
                      <td>{getRoleDisplayName(u.role)}</td>
                      <td>
                        <button className="btn-sm" onClick={() => setViewUser(u)} title="View"><FaEye /></button>
                        <button className="btn-sm" onClick={() => startEditUser(u)} title="Edit"><FaEdit /></button>
                        <button className="btn-sm btn-danger" onClick={() => handleDeleteUser(u._id)} title="Delete"><FaTrash /></button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>No asset users found</td>
                  </tr>
                )}
              </tbody>
            </table>

            {totalUserPages > 1 && (
              <div className="pagination">
                <button disabled={usersPage <= 1} onClick={() => setUsersPage(p => Math.max(1, p - 1))}>Prev</button>
                <span>Page {usersPage} / {totalUserPages}</span>
                <button disabled={usersPage >= totalUserPages} onClick={() => setUsersPage(p => Math.min(totalUserPages, p + 1))}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserListings;